import React from 'react';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios.config';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatDate = (s) => {
    if (!s) return null;
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return s;
    }
};

const formatGender = (g) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : g);

const DrivingLicenceV2OutputComponent = ({
    reportData,
    serviceInfo,
    isSharedView,
    hideCredits,
    // Supplied by SimpleReportForm when the host declares a `pdfEndpoint`. It
    // pulls the PDF through the authenticated axios instance, which is the only
    // path that works reliably — the legacy fallback below opens a public
    // `?token=…` URL that 401s once the cached token is gone.
    onDownloadPDF,
    pdfPasswordHint: pdfPasswordHintProp,
}) => {
    const shouldHideCredits = hideCredits || isSharedView;

    if (reportData?.code === '1004') {
        return <NoRecordsCard subtitle="There is no data found for the provided details. Please enter valid details." />;
    }

    let dlData = {};
    let dlResponse = {};
    let errorMessage = null;

    let parsedData = reportData;
    if (typeof reportData === 'string') {
        try { parsedData = JSON.parse(reportData); } catch { /* ignore */ }
    }

    if (parsedData?.data?.raw_response?.data?.data?.driving_license_data) {
        dlData = parsedData.data.raw_response.data.data.driving_license_data;
        dlResponse = parsedData.data.raw_response.data.data;
    } else if (parsedData?.data?.data?.driving_license_data) {
        dlData = parsedData.data.data.driving_license_data;
        dlResponse = parsedData.data.data;
    } else if (parsedData?.driving_license_data) {
        dlData = parsedData.driving_license_data;
        dlResponse = parsedData;
    } else if (parsedData?.raw_response?.data?.data?.driving_license_data) {
        dlData = parsedData.raw_response.data.data.driving_license_data;
        dlResponse = parsedData.raw_response.data.data;
    } else {
        const findNestedObj = (obj, key) => {
            if (obj && typeof obj === 'object') {
                if (key in obj) return obj[key];
                for (const k in obj) {
                    const found = findNestedObj(obj[k], key);
                    if (found) return found;
                }
            }
            return null;
        };
        const found = findNestedObj(parsedData, 'driving_license_data');
        if (found) {
            dlData = found;
            dlResponse = parsedData;
        } else {
            dlResponse = parsedData?.data || parsedData?.response || parsedData || {};
            dlData = dlResponse?.driving_license_data || dlResponse?.data?.driving_license_data || {};
        }
    }

    const remainingBalance =
        parsedData?.data?.remaining_credits ||
        parsedData?.remaining_credits ||
        serviceInfo?.available_credits;
    const code =
        parsedData?.data?.code ||
        parsedData?.code ||
        dlResponse?.code;
    const message = parsedData?.data?.message || parsedData?.message || dlResponse?.message;

    const isError = !reportData?.success || code === '1001' || code === '1004';
    if (isError) {
        errorMessage = reportData?.data?.api_message || (code !== '1000' ? message : null) || 'Driving licence verification failed';
    }

    const validity = dlData.validity || {};
    const rtoDetails = dlData.rto_details || {};
    const vehicleClasses = dlData.vehicle_class_details || dlData.covDetails || [];

    const pdfPasswordHint =
        pdfPasswordHintProp ||
        reportData?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        reportData?.response?.pdf_password_hint ||
        reportData?.report_json?.pdf_password_hint ||
        null;

    if (errorMessage && (!dlData || Object.keys(dlData).length === 0)) {
        return (
            <Card>
                <div className="text-center mb-3">
                    <i className="pi pi-times-circle text-5xl text-red-500 block mb-3" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-900 m-0">Driving Licence V2 Verification Failed</h3>
                </div>
                <Message severity="error" text={errorMessage} className="w-full mb-3" />
                <p className="text-700 text-sm m-0 mb-3">Unable to verify driving licence using GridLines advanced verification service.</p>
                <div className="surface-100 border-round p-3">
                    <h5 className="text-900 text-sm font-semibold m-0 mb-2">Service Information</h5>
                    <p className="text-600 text-sm m-0 mb-1"><strong>Provider:</strong> GridLines Technology</p>
                    <p className="text-600 text-sm m-0 mb-1"><strong>Service Type:</strong> Advanced Driving Licence Verification V2</p>
                    {code && <p className="text-600 text-sm m-0 mb-1"><strong>Error Code:</strong> {code}</p>}
                    <p className="text-600 text-sm m-0"><strong>Data Source:</strong> Enhanced Transport Department Database</p>
                </div>
                <div className="surface-200 border-round p-2 mt-3">
                    <p className="text-700 text-sm m-0">
                        <i className="pi pi-info-circle mr-2" aria-hidden="true" />
                        Alternative: Try basic Driving Licence verification (Tartan) if GridLines fails.
                    </p>
                </div>
            </Card>
        );
    }

    const handleDownloadPDF = async () => {
        // Authenticated download wins whenever the host wired one up.
        if (typeof onDownloadPDF === 'function') {
            onDownloadPDF();
            return;
        }

        const downloadLink =
            reportData?.pdf_url ||
            reportData?.pdf_download_url ||
            reportData?.download_link ||
            reportData?.download_url ||
            reportData?.data?.pdf_url ||
            reportData?.data?.pdf_download_url ||
            reportData?.data?.download_link ||
            reportData?.response?.pdf_download_url ||
            reportData?.api_response?.pdf_download_url ||
            reportData?.report_json?.pdf_download_url ||
            parsedData?.pdf_download_url ||
            parsedData?.pdf_url ||
            reportData?.data?.data?.pdf_download_url;

        if (!downloadLink) {
            toast.error('PDF download URL not available');
            return;
        }

        try {
            toast.info('Preparing PDF download...');
            if (downloadLink.startsWith('http')) {
                window.open(downloadLink, '_blank', 'noopener,noreferrer');
                toast.success('Download started');
                return;
            }
            const urlObj = new URL(downloadLink, window.location.origin);
            const apiPath = urlObj.pathname;
            const response = await axiosInstance.get(apiPath, {
                responseType: 'blob',
                headers: { Accept: 'application/pdf' },
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `driving_licence_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded successfully');
        } catch (err) {
            const status = err.response?.status;
            const msg =
                status === 500 ? 'Internal server error. Please try again later.' :
                status === 404 ? 'Report not found. Please generate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                err.response?.data?.message || 'Internal server error. Please try again later.';
            toast.error(msg);
        }
    };

    const hasPdf = !!(
        onDownloadPDF ||
        reportData?.pdf_download_url ||
        reportData?.pdf_url ||
        reportData?.data?.pdf_download_url ||
        reportData?.data?.pdf_url
    );

    return (
        <div>
            <SmartHeader title="Driving Licence V2 Verification Report" icon="pi-id-card" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            {errorMessage && code !== '1000' && (
                <div className="mb-3">
                    <Message severity="error" text={errorMessage} className="w-full" />
                </div>
            )}

            <Card>
                <Section icon="pi-user" title="Personal Details">
                    <SmartField name="full_name" value={dlData.name || dlData["Holder's Name"]} />
                    <SmartField name="father_name" value={dlData.dependent_name || dlData["Father's Name"]} />
                    <SmartField name="date_of_birth" value={formatDate(dlData.date_of_birth) || dlData['Date of Birth']} />
                    <SmartField name="gender" value={formatGender(dlData.gender || dlData.Gender)} />
                    <SmartField name="blood_group" value={dlData.blood_group || dlData['Blood Group']} />
                    <SmartField name="citizenship" value={dlData.citizenship || dlData.Citizenship} />
                </Section>

                <Section icon="pi-id-card" title="License Information">
                    <SmartField
                        name="license_number"
                        value={dlData.document_id || dlData.license_number || dlData['DL Number']}
                        highlight
                    />
                    <SmartField
                        name="issue_date"
                        value={validity.non_transport?.issue_date ? formatDate(validity.non_transport.issue_date) : dlData.DOI}
                    />
                    <SmartField
                        name="expiry_date"
                        value={validity.non_transport?.expiry_date ? formatDate(validity.non_transport.expiry_date) : dlData['Date of Expiry']}
                    />
                    <SmartField name="state" value={rtoDetails.state || dlData.State} />
                    <SmartField name="rto_authority" value={rtoDetails.authority || dlData['Last Transaction at']} />
                    {(validity.non_transport || validity['Non Transport']) && (
                        <SmartField
                            name="non_transport_validity"
                            value={
                                validity.non_transport
                                    ? `${formatDate(validity.non_transport.issue_date) || 'N/A'} to ${formatDate(validity.non_transport.expiry_date) || 'N/A'}`
                                    : `${validity['Non Transport'].From || 'N/A'} to ${validity['Non Transport'].To || 'N/A'}`
                            }
                        />
                    )}
                    {(validity.transport || validity.Transport) && (
                        <SmartField
                            name="transport_validity"
                            value={
                                validity.transport
                                    ? `${formatDate(validity.transport.issue_date) || 'N/A'} to ${formatDate(validity.transport.expiry_date) || 'N/A'}`
                                    : `${validity.Transport.From || 'N/A'} to ${validity.Transport.To || 'N/A'}`
                            }
                        />
                    )}
                </Section>

                <Section icon="pi-home" title="Address & Contact">
                    {(dlData.address || dlData.Address) && (
                        <div className="col-12 flex flex-column gap-1">
                            <span className="text-500 text-xs font-medium">Complete Address</span>
                            <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>
                                {dlData.address || dlData.Address}
                            </span>
                        </div>
                    )}
                    <SmartField name="location" value={dlData.location || dlData.Location} />
                    <SmartField name="city" value={dlData.city || dlData.City} />
                    <SmartField name="pin_code" value={dlData.pin_code || dlData['Zip Code']} />
                </Section>

                <Section icon="pi-car" title="Vehicle Classes (COV)">
                    <div className="col-12 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Authorized Vehicle Classes</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {vehicleClasses && vehicleClasses.length > 0 ? (
                                vehicleClasses.map((cov, index) => (
                                    <Chip key={index} label={cov.category || cov['Class of Vehicle'] || 'N/A'} />
                                ))
                            ) : (
                                <span className="text-500 text-sm">No vehicle classes available</span>
                            )}
                        </div>
                    </div>
                </Section>

                <div className="text-center pt-3">
                    <Button
                        label="Download Driving Licence v2 Report"
                        icon="pi pi-download"
                        onClick={handleDownloadPDF}
                        disabled={!hasPdf}
                        size="small"
                    />
                    {pdfPasswordHint && (
                        <div className="mt-2">
                            <Message severity="info" text={pdfPasswordHint} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default DrivingLicenceV2OutputComponent;
